import { Routes, Route, useLocation, Navigate } from "react-router-dom";
import { ThemeProvider } from "@/components/theme-provider";
import LoginPage from "./pages/auth/LoginPage";
import DashboardPage from "./pages/dashboard/DashboardPage";
import AdminPage from "./pages/dashboard/AdminPage";
import StatisticsPage from "./pages/dashboard/StatisticsPage";
import NotFoundPage from "./pages/NotFoundPage";
import { AuthProvider, useAuth } from "./global-hooks/useAuth";
import { hasPermission } from "@/lib/permissions";
import { LoadingSpinner } from "@/components/ui/spinner";
import { PropsWithChildren, useEffect } from "react";
import { Toaster } from "sonner";
import { ErrorBoundary, GlobalErrorHandler } from "@/lib/global-error-handler";
import { USER_ROLES } from "@/lib/roles";
import { H } from "highlight.run";

H.init("4g88pkjg", {
	serviceName: "student-papers-frontend",
	tracingOrigins: true,
	environment: process.env.VITE_APP_ENV === "staging" ? "staging" : "production",
	networkRecording: {
		enabled: true,
		recordHeadersAndBody: true,
		urlBlocklist: [
			"https://www.googleapis.com/identitytoolkit",
			"https://securetoken.googleapis.com",
		],
	},
});

function RequireAuth({ children }: PropsWithChildren) {
	const auth = useAuth();
	const location = useLocation();

	if (auth.isPending) {
		return (
			<div className="flex items-center justify-center min-h-screen">
				<LoadingSpinner message="Đang đăng nhập..." />
			</div>
		);
	}

	if (!auth.isAuthenticated) {
		return <Navigate to="/auth/login" state={{ from: location }} replace />;
	}

	return children ? <>{children}</> : <NotFoundPage />;
}

function RequireAdminOrManager({ children }: PropsWithChildren) {
	const auth = useAuth();
	const location = useLocation();

	if (auth.isPending) {
		return (
			<div className="flex items-center justify-center min-h-screen">
				<LoadingSpinner message="Đang đăng nhập..." />
			</div>
		);
	}

	if (!auth.isAuthenticated) {
		return <Navigate to="/auth/login" state={{ from: location }} replace />;
	}

	if (auth.user?.role !== "admin" && auth.user?.role !== "manager") {
		return <Navigate to="/dashboard" replace />;
	}

	return children ? <>{children}</> : <NotFoundPage />;
}

// Named export purely for testability — `App.test.tsx` renders this in
// isolation to pin the additive `role === "user" || access_dashboard`
// guard without dragging in the rest of the route tree.
export function RequireDashboardAccess({ children }: PropsWithChildren) {
	const auth = useAuth();
	const location = useLocation();

	if (auth.isPending) {
		return (
			<div className="flex items-center justify-center min-h-screen">
				<LoadingSpinner message="Đang đăng nhập..." />
			</div>
		);
	}

	if (!auth.isAuthenticated) {
		return <Navigate to="/auth/login" state={{ from: location }} replace />;
	}

	// `role === "user"` (reception desk) is admitted unconditionally, unchanged.
	// The `access_dashboard` permission is an ADDITIVE grant on top of that — never a
	// replacement — for accounts whose role does not already carry access
	// (manager / ban giám hiệu). Do not fold this into `admin || cờ`: that would
	// revoke `/dashboard` from every existing reception-desk account.
	//
	// `viewer` phải nằm ở đây, không phải tuỳ chọn: dashboard là nơi DUY NHẤT vai
	// trò này được vào. Thiếu nó thì nhánh dưới đẩy viewer sang `/statistics`, mà
	// `RequireAdminOrManager` lại đẩy ngược về `/dashboard` — vòng lặp redirect vô
	// hạn, không phải một trang "không đủ quyền".
	const canAccessDashboard =
		auth.user?.role === USER_ROLES.admin ||
		auth.user?.role === USER_ROLES.user ||
		auth.user?.role === USER_ROLES.viewer ||
		hasPermission(auth.user?.permissions, "access_dashboard");

	if (!canAccessDashboard) {
		return <Navigate to="/statistics" replace />;
	}

	return children ? <>{children}</> : <NotFoundPage />;
}

function RoleBasedRedirect() {
	const auth = useAuth();

	if (auth.isPending) {
		return (
			<div className="flex items-center justify-center min-h-screen">
				<LoadingSpinner message="Đang đăng nhập..." />
			</div>
		);
	}

	if (!auth.isAuthenticated) {
		return <Navigate to="/auth/login" replace />;
	}

	// Redirect users based on role
	let redirectPath = "/dashboard";
	if (auth.user?.role === "manager") {
		redirectPath = "/statistics";
	}

	return <Navigate to={redirectPath} replace />;
}

function App() {
	// Initialize global error handler
	useEffect(() => {
		GlobalErrorHandler.getInstance();
	}, []);

	return (
		<ErrorBoundary>
			<ThemeProvider>
				<AuthProvider>
					<div className="min-h-screen bg-background">
						<Routes>
							<Route path="/" element={<RoleBasedRedirect />} />
							<Route path="/auth/login" element={<LoginPage />} />
							{/* MỘT route với tham số tuỳ chọn, không phải hai route cùng trỏ vào
								`DashboardPage`. Tham số chỉ để mở sẵn một hồ sơ — để
								`/dashboard/26120259` gửi cho người khác vẫn mở đúng.

								Tách làm hai route thì hai `element` là hai phần tử khác nhau, nên
								đi từ `/dashboard/:mssv` sang `/dashboard` khiến React Router
								unmount trang này rồi mount lại — mất sạch state. Đó không phải
								chuyện lý thuyết: khi tra hỏng, `student-search.tsx` vừa dựng
								thông báo lỗi vừa gỡ MSSV khỏi URL, và cú gỡ URL ấy xoá luôn thông
								báo vừa dựng. Người dùng mở một đường dẫn hỏng sẽ thấy màn hình
								trống trơn, không một lời giải thích. */}
							<Route
								path="/dashboard/:mssv?"
								element={
									<RequireDashboardAccess>
										<DashboardPage />
									</RequireDashboardAccess>
								}
							/>
							<Route
								path="/admin"
								element={
									<RequireAuth>
										<AdminPage />
									</RequireAuth>
								}
							/>
							<Route
								path="/statistics"
								element={
									<RequireAdminOrManager>
										<div className="min-h-screen bg-background">
											<StatisticsPage />
										</div>
									</RequireAdminOrManager>
								}
							/>
							{/* Catch-all route for 404 */}
							<Route path="*" element={<NotFoundPage />} />
						</Routes>
						<Toaster position="top-right" richColors closeButton duration={4000} />
					</div>
				</AuthProvider>
			</ThemeProvider>
		</ErrorBoundary>
	);
}

export default App;
